/**
 * This is one of the Standard Widgets in the Web Widgets Framework. It uses the text field widget
 * and a custom template.
 *
 * TextFieldMenuItem widget is a child widget and these widgets are supposed to be used with in a Menu.
 * So, a Menu must be created first and then TextFieldMenuItem's should be added to the Menu.<br>
 * To see all the available properties for this widget, please look at the corresponding Property Mixins below.
 * <br>
 * <br>
 * <strong>Property Mixins:</strong><br>
 * {@link module:mw-form/mixins/property/PlaceholderMixin}<br>
 * {@link module:mw-form/mixins/property/EditableMixin}<br>
 * {@link module:mw-form/mixins/property/CloseMenuOnClickMixin}<br>
 * {@link module:mw-form/mixins/property/TagMixin}<br>
 * {@link module:mw-form/mixins/property/DisabledMixin}<br>
 * {@link module:mw-form/mixins/property/ShortcutMixin}<br>
 * {@link module:mw-form/mixins/property/ShowShortcutMixin}<br>
 * {@link module:mw-form/mixins/property/ShowTextMixin}<br>
 * {@link module:mw-form/mixins/property/TextMixin}<br>
 * {@link module:mw-form/mixins/property/VisualFamilyMixin}<br>
 * {@link module:mw-form/mixins/property/ValidationMixin}<br>
 * <strong>Widgets</strong><br>
 * {@link module:mw-form/TextFieldMenuItem}<br>

 *
 * <strong> Events: </strong>
 * TextFieldMenuItem emits a:
 * <li>change ({@link module:mw-form/mixins/event/ChangeEventMixin})<br>
 * The event is triggered when the user changes the content and either presses "ENTER" or the widget loses focus.
 * </li>
 *
 * <li>commit<br>
 * The event is triggered when the user changes the content and presses "ENTER".
 * It contains the "mwEventData" object that contains the "propertyName" and "value" properties.
 * </li>
 *
 * <li>recommit<br>
 * The event is triggered when the user does not change the content and presses "ENTER".
 * It contains the "mwEventData" object that contains the "propertyName" and "value" properties.
 * </li>
 *
 * <li>typing<br>
 * Th event is triggered while the user is typing. It contains the "mwEventData" object that contains the "currentContent" property.
 * </li>
 * @example <caption>Creating a new TextFieldMenuItem</caption>
 * var TextFieldMenuItem = new TextFieldMenuItem({
 *     value: "Sample Text"
 * });
 *
 * var Menu1 = new Menu({
       maxHeight: 600
 * });
 * Menu1.add(TextFieldMenuItem);
 * TextFieldMenuItem.startup();
 *
 * TextFieldMenuItem.on("change", function(e){
 *   console.dir(e.mwEventData);
 * });
 *
 * TextFieldMenuItem.on("typing", function(e){
 *   console.log(e.mwEventData.currentContent);
 * });
 * @module mw-form/TextFieldMenuItem
 *
 * @copyright Copyright 2015-2018 The MathWorks, Inc.
 */

// TODO: Remove _CSSStateMixin dependency, as seen in SplitButton and ToggleSplitButton

define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/on",
    "dojo/keys",
    "dijit/focus",
    "dijit/_WidgetBase",
    "dijit/_TemplatedMixin",
    "dijit/_CssStateMixin",
    "dijit/_Contained",
    "dijit/_FocusMixin",
    "mw-dom-utils/domUtils",
    "mw-form/TextField",
    "mw-form/mixins/property/CloseMenuOnClickMixin",
    "mw-form/mixins/property/TagMixin",
    "mw-form/mixins/property/DisabledMixin",
    "mw-form/mixins/property/DescriptionMixin",
    "mw-form/mixins/property/ShortcutMixin",
    "mw-form/mixins/property/ShowTextMixin",
    "mw-form/mixins/property/ShowShortcutMixin",
    "mw-form/mixins/property/TextMixin",
    "mw-form/mixins/property/VisualFamilyMixin",
    "mw-form/mixins/FocusableMenuChildMixin",
    "mw-form/mixins/MenuChildResizeMixin",
    "mw-form/mixins/PreventSelectionMixin",
    "mw-form/mixins/MenuItemAriaLabelledByMixin",
    "mw-form/mixins/mixinDependencyValidator",
    "mw-form/mixins/property/ValidationMixin",

    "dojo/text!./templates/TextFieldMenuItem.html"
], function (declare, lang, on, keys, focusUtil, _WidgetBase, _TemplatedMixin, _CssStateMixin, _Contained, _FocusMixin,
             domUtils, TextField, CloseMenuOnClickMixin, TagMixin, DisabledMixin, DescriptionMixin, ShortcutMixin, ShowTextMixin,
             ShowShortcutMixin, TextMixin, VisualFamilyMixin, FocusableMenuChildMixin, MenuChildResizeMixin,
             PreventSelectionMixin, MenuItemAriaLabelledByMixin, mixinDependencyValidator, ValidationMixin, template) {

    return declare(mixinDependencyValidator.validate([_WidgetBase, _TemplatedMixin, _CssStateMixin, _Contained, _FocusMixin,
        DescriptionMixin, ShortcutMixin, ShowShortcutMixin, ShowTextMixin, TextMixin, CloseMenuOnClickMixin, TagMixin,
        DisabledMixin, VisualFamilyMixin, FocusableMenuChildMixin, MenuChildResizeMixin, PreventSelectionMixin,
        MenuItemAriaLabelledByMixin, ValidationMixin]), {

        baseClass: "mwWidget mwSharedMenuItem mwTextFieldMenuItem",
        templateString: template,
        closeOnEnter: true,

        //Editable and Placeholder are handled separately
        //not using mixins are mixins are not smart enough to handle composition.

        editable: true,
        placeholder: "",


        //Change event of check box causes drop downs to close.
        ///Put break point in Closedropdown of hasdropdown
        constructor: function () {
            this.textField = new TextField({
                value: ""
            });
            this.own(this.textField);
        },

        //Did not use placeholder mixin as this widget is a composition
        //PlaceholderMixin sets the property on the widget itself, but here the property should be set on the widgets textfield
        _setPlaceholderAttr: function (value) {
            this.textField.set("placeholder", value);
            this._set("placeholder", value);
        },

        _getPlaceholderAttr: function (value) {
            return this.textField.get("placeholder");
        },

        _setValueAttr: function (value) {
            this.textField.set("value", value);
            this._set("value", value);
        },

        _setEditableAttr: function (value) {
            // the editable setter adds readOnly attribute to the inputNode on the dom.
            this.textField.set("editable", value);
            // this could potentially be this.inherited(arguments) like the disabled setter below,
            // but doing that would duplicate a call to add readOnly attribute to the inputNode on the dom.
            this._set("editable", value);
        },

        _setDisabledAttr: function (value) {
            this.textField.set("disabled", value);
            this.inherited(arguments);
        },

        _getValueAttr: function () {
            return this.textField.get("value");
        },

        _getEditableAttr: function () {
            return this.textField.get("editable");
        },

        buildRendering: function () {
            this.inherited(arguments);
            
            this.textFieldWrapperNode.appendChild(this.textField.domNode);
            
            this.inputNode = this.textField.inputNode;

            // Add a custom class to avoid any code in the mixins to do a preventDefault on the inputNode. See g2077647.
            this.inputNode.classList.add("allowDefaultActions");
        },

        postCreate: function () {
            this.inherited(arguments);
            this.textField.on("change", lang.hitch(this, function (event) {
                // Avoid setting the value if it was already set by another event.
                if (this.get("value") !== event.mwEventData.newValue) {
                    this.set("value", event.mwEventData.newValue);
                }
            }));
            this.own(
                on(this.textField.focusNode, "change", lang.hitch(this, "_onChange")),
                on(this.textField.focusNode, "keydown", lang.hitch(this, "_onKeyDown")),
                on(this.domNode, "focus", lang.hitch(this, "_focusGained"))
            );

            this.domNode.classList.add("mwEditableMixin");
            // When the default value is falsy, the setter will not be triggered during the widget's construction phase
            // We need to call the setter explicitly
            this._setEditableAttr(this.get("editable"));
            this._setPlaceholderAttr(this.get("placeholder"));
            this.domNode.setAttribute("data-composite-primary", "");
            //needed because we've created an instance of MW/form/textfield and the spellcheck attribute is missing.
            //MW/toolstrip/form/TextField sets the spellcheck attribute but TextFieldMenuItem is an instance of the form/TextField
            this.domNode.setAttribute("spellcheck", "false");
            (this.textField.focusNode || this.textField.domNode).setAttribute("data-composite-child", "");
            (this.textField.focusNode || this.textField.domNode).setAttribute("data-composite-primary-id", this.get("id"));
            // TextField focusNode is a child of the domNode, and so these attributes should be added to both nodes for completeness
            (this.textField.domNode).setAttribute("data-composite-child", "");
            (this.textField.domNode).setAttribute("data-composite-primary-id", this.get("id"));

            this.textField.focusNode.setAttribute("aria-labelledby", this.get("id"));
        },

        startup: function () {
            this.inherited(arguments);
            this.textField.startup();
        },

        //For validation Mixin
        _getDomNodeForValidationClasses: function () {
            return this.textField.domNode;
        },

        // g1336364, g1350143 - Moved from FocusableMenuChild because this logic was being called
        // on click after the action was performed, and therefore was causing the actions to be
        // nullified.
        // This logic is okay in TextFieldMenuItem because clicking a TextFieldMenuItem does not
        // perform an action.
        _focusGained: function (/*Event*/ e) {
            // Called when the widget.domNode is focused on hover, keynav, or click, but
            // TextFieldMenuItem wants its focus to be in the child input node, so forward the focus
            // call to widget.focus()
            if (this.focus && domUtils.isFocusable(this.focusNode || this.domNode)) {
                this.focus();
            }
        },

        focus: function () {
            var node = this.focusNode || this.domNode;

            // TODO: Update logic when CompositionMixin is added
            // If the node is a child in a composite widget, then get the primary node from the value of the "data-composite-primary-id" attribute
            if (node.hasAttribute("data-composite-child")) {
                node = document.getElementById(node.getAttribute("data-composite-primary-id"));
            }

            if (domUtils.isFocusable(node)) {
                focusUtil.focus(this.getFocusNode());
            }
        },

        getFocusNode: function () {
            return this.textField.focusNode;
        },

        _onKeyDown: function (e) {
            // Popup.js closes the popup when TAB is pressed. Handling the case here
            if (e.keyCode === keys.TAB) {
                e.preventDefault();
                e.stopPropagation();

                // Since we have stopped propagation, we need to emit a synthetic method
                this.emit("synkeydown", {keyCode: keys.TAB, shiftKey: e.shiftKey});
            } else if (e.keyCode === keys.ESCAPE) {
                this.textField.inputNode.value = this.get("value");
                this.emit("textcancelled", {
                    mwEventData: {
                        text: this.get("value")
                    }
                });
                //Stop propagation to prevent emitting event twice
                e.preventDefault();
                e.stopPropagation();

                this._requestClose(false);
            } else if (e.keyCode === keys.ENTER) {
                // g1588429 - Update the events being fired to be easier to identify the source
                var oldValue = this.get("value"),
                    newValue = this.textField.inputNode.value;

                if (oldValue === newValue) {
                    // Emit a "recommit" event in case someone wants to listen for all [Enter] key presses.
                    this.emit("recommit", {mwEventData: {propertyName: "value", value: newValue}});
                } else {
                    // Emit a "commit" event signaling that the [Enter] key was hit.  And, commit the value.
                    this.emit("commit", {mwEventData: {propertyName: "value", value: newValue}});
                    this.set("value", newValue);

                    // Emit a change event for backwards compatability
                    this.textField._triggerChangeEvent("value", oldValue, newValue);
                }

                if (this.closeOnEnter && (this.closeOnEnter === true)) {
                    this._requestClose(false);
                }
            } else if ((e.keyCode === keys.HOME) || (e.keyCode === keys.END)) {
                // g1590057 - Prevent the KeyNavMixin from handling the Home and End keys
                e.stopPropagation();
            }
        },

        //Select entire text
        _handleInputNodeClick: function () {
            if (this.textField.get("editable") !== false) {
                this.textField.inputNode.select();
            }
        },

        _emitClickEvent: function (evt) {
            this.emit("click", {
                mwEventData: {
                    value: this.get("value")
                },
                _dojo_click: true
            });

            if (evt.target !== this.inputNode) {
                this.emit("itempushed");
            }
        },

        _qeTypeOverWithEnter: function (value) {
            this.textField._qeTypeOverWithEnter(value);
        },

        _qeTyping: function(value) {
            this.textField._qeTyping(value);
        }

    });

});
